import "./receptionTypes.js";
import React, { memo } from "react";
import { Box, Tooltip } from "@mui/material";
import { status } from "shared/src/appointment.mjs";

import * as f from "../../utils/formatter";
import Link from "../Link";
import ServiceTag from "../ServiceTag";
import IconAngry from "../icons/IconAngry.jsx";
import IconLanguage from "../icons/IconLanguage.jsx";
import IconSeat from "../icons/IconSeat.jsx";
import IconNotes from "../icons/IconNotes.jsx";
import IconIssue from "../icons/IconIssue.jsx";
import SpecialDay from "../client/SpecialDay.jsx";
import {
  blockerText,
  getForeignSpeakingOnlyTitle,
  hasBlocker,
} from "shared/src/client.mjs";
import { isGrantedAny, role } from "shared/src/roles.mjs";
import { useRoles } from "../../context/AuthenticationTokenContext";
import VirtualCareTag from "../VirtualCareTag.jsx";
import { useClientProblem } from "../client/hooks/useClientProblem.jsx";

const ClientName = memo(
  /**
   * A component to display client name and appointment details
   *
   * @typedef {object} ClientNameProps
   * @property {Appointment} appointment
   * @property {Set<string>} nameDayNames
   * @property {boolean} [displayMinimalInfo=false]
   *
   * @param {ClientNameProps} props
   * @returns {JSX.Element}
   */
  ({ appointment, nameDayNames, displayMinimalInfo = false }) => {
    const client = appointment.client;
    const languages = getForeignSpeakingOnlyTitle(client);
    const roles = useRoles();
    const problem = useClientProblem(client, roles);
    const canSeeSpecialDay = isGrantedAny(
      roles,
      new Set([
        role.Treatment_Coordinator,
        role.Back_Office,
        role.Front_Office,
        role.Dental_Assistant,
        role.Manager_Clinic,
        role.Manager_Customer_Care,
        role.Read_Only,
        role.Read_Only_Full,
      ]),
    );
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "0.5em",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <VirtualCareTag client={client} />
          <Link
            to={"/clients/" + appointment.client.id}
            style={{
              color: "inherit",
              textDecoration: "none",
              fontWeight: "bold",
              fontSize: "14px",
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              minWidth: "0",
            }}
          >
            <Box
              sx={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
              }}
            >
              {f.fullName(appointment.client)}
            </Box>
          </Link>
          {(appointment.status_id === status.BOOKED ||
            appointment.status_id === status.CONFIRMED) &&
            hasBlocker(client) && (
              <Tooltip title={blockerText(client)}>
                <IconIssue />
              </Tooltip>
            )}
          {!displayMinimalInfo && canSeeSpecialDay && (
            <SpecialDay client={client} nameDayNames={nameDayNames} />
          )}
          {languages != null && (
            <Tooltip title={languages}>
              <IconLanguage />
            </Tooltip>
          )}
          {problem != null && (
            <Tooltip title={problem}>
              <IconAngry />
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            maxWidth: "100%",
          }}
        >
          <ServiceTag service={appointment.service} />
          <IconNotes appointment={appointment} />
          {appointment.status_id === status.ASSIGNED && <IconSeat />}
        </Box>
      </Box>
    );
  },
);

ClientName.displayName = "ClientName";
export { ClientName };
